/**
 * <TrialOfferCard> — l'offre Essentielle proposée à l'élève qui n'a pas d'abonnement (molecule).
 *
 * La carte d'événement du livre (`<EventCard>`) avec les mots que l'élève lit aujourd'hui, écrits
 * une seule fois : « Messages » et ses réalisations montrent la même carte. Elle n'a qu'un
 * bouton, le seul que l'élève atteint (la carte n'apparaît qu'à qui peut démarrer un essai).
 *
 * **En pause, la même carte dit la pause** (`onAskQuestion`) : l'élève dont l'abonnement est
 * suspendu a perdu son offre le temps de la pause, il lit donc cette carte, qui lui donne la
 * période et « Une question ? » à la place de l'essai (`BecomePremiumCard.js`).
 *
 * Elle ne dessine pas son cadre : c'est la page qui l'encadre (`<HighlightFrame>`).
 */
import React from "react"

import { formatDayMonthYear } from "../../../ports/primary/ui/formatters/dateFormatter"
import { type UIPausePeriod } from "../../../ports/primary/ui/types/studentTutoring"
import { EventCard } from "../EventCard"

export type { UIPausePeriod }

export type TrialOfferCardProps = { testID?: string } & (
    | {
          /** Ouvre le démarrage de l'essai. */
          onStartTrial: () => void
          onAskQuestion?: undefined
          pausePeriod?: undefined
      }
    | {
          /** « Une question ? » : l'abonnement est en pause, la carte le dit à la place de l'offre. */
          onAskQuestion: () => void
          /** Absente quand la reprise ne suit pas le début de la pause : la phrase s'arrête à « en pause ». */
          pausePeriod?: UIPausePeriod
          onStartTrial?: undefined
      }
)

const PAUSED_MESSAGE = "Ton abonnement est en pause"

export function TrialOfferCard(props: TrialOfferCardProps) {
    const { testID } = props

    if (props.onAskQuestion != null) {
        const { pausePeriod, onAskQuestion } = props
        return (
            <EventCard
                testID={testID}
                title="Abonnement en pause"
                message={
                    pausePeriod != null
                        ? `${PAUSED_MESSAGE} du ${formatDayMonthYear(pausePeriod.start, false)} au ${formatDayMonthYear(pausePeriod.end, false)}`
                        : PAUSED_MESSAGE
                }
                actionLabel="Une question ?"
                onAction={onAskQuestion}
            />
        )
    }

    return (
        <EventCard
            testID={testID}
            title="Offre Essentielle"
            message="Bénéficie d'un suivi sur mesure par un tuteur"
            actionLabel="Essai gratuit"
            onAction={props.onStartTrial}
        />
    )
}
